import React from "react";
import { PageHeader } from "@finitless/shared/components";
import { useTranslation } from "react-i18next";
import {
  Button,
  Icon,
  Switch,
  Skeleton,
  PaymentsHowItWorksModal,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "../ui";
import { usePostHog } from "@finitless/shared/hooks";
import { useStripeConnect } from "../../hooks/useStripeConnect";
import "./online-payments-settings.css";

const OnlinePaymentsSettingsPage = ({ selectedBrand }) => {
  const { t } = useTranslation("settings");
  const { isFeatureEnabled } = usePostHog();
  const showSandboxControl = isFeatureEnabled(
    "stripe_connect_link_sandbox_control",
  );
  const [isPaymentsActive, setIsPaymentsActive] = React.useState(true);
  const [isSandbox, setIsSandbox] = React.useState(false);
  const [isRedirectingToStripe, setIsRedirectingToStripe] =
    React.useState(false);
  const [isHowItWorksModalOpen, setIsHowItWorksModalOpen] =
    React.useState(false);
  const didAutoInstallRef = React.useRef(false);

  const {
    useStripeConnectPlugins,
    useInstallStripeConnectPlugin,
    useTogglePluginEnabled,
    usePaymethod,
    useTogglePaymethodEnabled,
    useStatus,
    useOnboardingLink,
    useSandboxMode,
  } = useStripeConnect();

  const {
    data: plugins,
    isLoading: isLoadingPlugins,
    isError: isPluginsError,
    error: pluginsError,
    refetch: refetchPlugins,
  } = useStripeConnectPlugins();

  const { mutate: installStripeConnectPlugin, isPending: isInstallingPlugin } =
    useInstallStripeConnectPlugin();

  const { isPending: isTogglingPlugin } = useTogglePluginEnabled();

  const { mutate: toggleSandboxMode, isPending: isTogglingSandbox } =
    useSandboxMode();

  const hasStripeConnectPlugin = (plugins?.length ?? 0) > 0;
  const isPluginsLoading = isLoadingPlugins;
  const stripeConnectPlugin = plugins?.[0] ?? null;
  const stripeConnectPluginId = stripeConnectPlugin?.id;
  const stripeConnectPluginEnabled = !!stripeConnectPlugin?.enabled;

  React.useEffect(() => {
    if (didAutoInstallRef.current) {
      return;
    }
    if (isPluginsLoading) {
      return;
    }
    if (isPluginsError) {
      return;
    }
    if (hasStripeConnectPlugin) {
      return;
    }
    if (isInstallingPlugin) {
      return;
    }

    didAutoInstallRef.current = true;
    installStripeConnectPlugin({});
  }, [
    hasStripeConnectPlugin,
    installStripeConnectPlugin,
    isInstallingPlugin,
    isPluginsLoading,
    isPluginsError,
  ]);

  const {
    data: status,
    isLoading: isLoadingStatus,
    isFetching: isFetchingStatus,
    refetch: refetchStatus,
  } = useStatus({
    enabled:
      !isPluginsLoading &&
      !isInstallingPlugin &&
      hasStripeConnectPlugin &&
      stripeConnectPluginEnabled &&
      !isTogglingPlugin,
  });

  const { data: paymethod, refetch: refetchPaymethod } = usePaymethod({
    enabled:
      hasStripeConnectPlugin &&
      !isPluginsLoading &&
      !isInstallingPlugin &&
      !isLoadingStatus &&
      !!status,
  });

  const { mutate: togglePaymethodEnabled, isPending: isTogglingPaymethod } =
    useTogglePaymethodEnabled();

  const paymethodId = paymethod?.id;

  React.useEffect(() => {
    if (paymethod?.enabled !== undefined) {
      setIsPaymentsActive(!!paymethod.enabled);
    }
  }, [paymethod?.enabled, paymethod?.id]);

  const { mutate: getOnboardingLink, isPending: isCreatingLink } =
    useOnboardingLink();

  const isRefreshing = isLoadingStatus || isFetchingStatus;

  const handleRefreshStatus = async () => {
    await refetchStatus();
    refetchPaymethod();
  };

  const statusValue = status?.status || "not_started";
  const isConnected = statusValue === "connected";
  const isIncomplete = statusValue === "incomplete";
  const requirements = status?.requirements || [];
  const isConnectedButSetupIncomplete =
    isConnected && (!status?.charges_enabled || !status?.payouts_enabled);
  const isFullyConnected =
    isConnected && !!status?.charges_enabled && !!status?.payouts_enabled;

  React.useEffect(() => {
    if (status?.sandbox !== undefined) {
      setIsSandbox(!!status.sandbox);
    }
  }, [status?.sandbox]);

  const handleSandboxChange = (nextSandbox) => {
    setIsSandbox(nextSandbox);
    toggleSandboxMode(
      { sandbox: nextSandbox },
      {
        onError: () => {
          setIsSandbox(!nextSandbox);
        },
      },
    );
  };

  const handleStartRedirect = () => {
    if (isRedirectingToStripe) {
      return false;
    }
    setIsRedirectingToStripe(true);
    return true;
  };

  const handlePaymentsActiveChange = (nextEnabled) => {
    if (!paymethodId) {
      return;
    }
    if (nextEnabled && !isFullyConnected) {
      return;
    }
    setIsPaymentsActive(nextEnabled);
    togglePaymethodEnabled(
      { paymethodId, enabled: nextEnabled },
      {
        onError: () => {
          setIsPaymentsActive(!nextEnabled);
        },
      },
    );
  };

  const handleConnectStripe = () => {
    const canProceed = handleStartRedirect();
    if (!canProceed) {
      return;
    }

    getOnboardingLink(
      {
        refresh_url: window.location.href,
        return_url: window.location.href,
      },
      {
        onSuccess: (data) => {
          if (data?.url) {
            window.location.href = data.url;
          }
        },
        onError: () => {
          setIsRedirectingToStripe(false);
        },
      },
    );
  };

  const LoadingSkeleton = () => (
    <div
      className="ordering-payments__loading space-y-6"
      aria-busy="true"
      role="status"
      data-testid="payments-loading"
    >
      <div className="ordering-payments__panel p-5 sm:p-6">
        <div className="flex flex-col justify-between gap-8 md:flex-row md:items-start">
          <div className="w-full flex-1 space-y-5">
            <div className="flex items-center gap-3">
              <Skeleton className="ordering-payments__skeleton h-10 w-10 rounded-md" />
              <Skeleton className="ordering-payments__skeleton h-7 w-64 max-w-[70vw]" />
            </div>
            <div className="space-y-3">
              <Skeleton className="ordering-payments__skeleton h-4 w-full max-w-md" />
              <Skeleton className="ordering-payments__skeleton h-4 w-full max-w-sm" />
              <Skeleton className="ordering-payments__skeleton h-4 w-full max-w-xs" />
            </div>
            <Skeleton className="ordering-payments__skeleton h-11 w-40 rounded-sm" />
          </div>
          <Skeleton className="ordering-payments__skeleton hidden h-40 w-48 rounded-lg lg:block" />
        </div>
      </div>

      <div className="ordering-payments__panel overflow-hidden">
        <div className="ordering-payments__panel-header flex justify-between p-5 sm:p-6">
          <div className="flex gap-3">
            <Skeleton className="ordering-payments__skeleton h-10 w-10 rounded-md" />
            <div className="space-y-2">
              <Skeleton className="ordering-payments__skeleton h-5 w-32" />
              <Skeleton className="ordering-payments__skeleton h-3 w-48 max-w-[50vw]" />
            </div>
          </div>
          <Skeleton className="ordering-payments__skeleton h-6 w-16 rounded-full" />
        </div>
        <div className="flex flex-col gap-8 p-5 sm:p-6 lg:flex-row">
          <div className="flex-1 space-y-5">
            <Skeleton className="ordering-payments__skeleton h-7 w-full max-w-md" />
            <div className="space-y-3">
              <Skeleton className="ordering-payments__skeleton h-4 w-full" />
              <Skeleton className="ordering-payments__skeleton h-4 w-5/6" />
            </div>
            <div className="space-y-3">
              <Skeleton className="ordering-payments__skeleton h-5 w-48" />
              <Skeleton className="ordering-payments__skeleton h-5 w-56 max-w-[60vw]" />
            </div>
          </div>
          <Skeleton className="ordering-payments__skeleton h-56 flex-1 rounded-lg" />
        </div>
      </div>
    </div>
  );

  const StripeActions = ({
    variant, // "connect" | "primary" | "manage"
    primaryText,
    onPrimaryClick,
    isPrimaryDisabled,
    isPrimaryLoading,
    onRefreshClick,
    isRefreshing: isRefreshingButton,
    onLearnHowItWorks,
  }) => {
    const showRefresh = typeof onRefreshClick === "function";
    const showLearn = typeof onLearnHowItWorks === "function";

    const isManage = variant === "manage";

    const loaderSize = isManage ? 14 : 16;

    return (
      <div
        className="ordering-payments__actions flex flex-wrap items-center gap-3"
        data-action-variant={variant}
      >
        <Button
          type="button"
          onClick={onPrimaryClick}
          disabled={isPrimaryDisabled}
          aria-busy={isPrimaryLoading || undefined}
          emphasis="solid"
          intent="brand"
          size="touch"
          className="ordering-payments__button ordering-payments__button--primary"
        >
          {isPrimaryLoading ? (
            <Icon name="Loader2" className="animate-spin" size={loaderSize} />
          ) : isManage ? (
            <Icon name="ExternalLink" size={14} />
          ) : null}
          {primaryText}
        </Button>

        {showRefresh ? (
          <Button
            type="button"
            onClick={onRefreshClick}
            disabled={isRefreshingButton}
            aria-busy={isRefreshingButton || undefined}
            emphasis="outline"
            intent="neutral"
            size="touch"
            className="ordering-payments__button ordering-payments__button--secondary"
          >
            <Icon
              name="RefreshCcw"
              className={isRefreshingButton ? "animate-spin" : ""}
              size={14}
            />
            {t("onlinePayments.actions.refreshStatus")}
          </Button>
        ) : null}

        {showLearn ? (
          <Button
            type="button"
            onClick={onLearnHowItWorks}
            className="ordering-payments__learn-button"
            aria-label={t("onlinePayments.actions.learnHowItWorks")}
            emphasis="link"
            intent="neutral"
            size="touch"
          >
            {t("onlinePayments.actions.learnHowItWorks")}
            <Icon name="ChevronRight" size={14} />
          </Button>
        ) : null}
      </div>
    );
  };

  return (
    <div
      className="ordering-payments"
      data-ordering-surface="online-payments"
      data-payment-state={statusValue}
    >
      <div className="ordering-payments__page-header">
        <PageHeader
          title={t("onlinePayments.header.title")}
          subtitle={t("onlinePayments.header.subtitle")}
          selectedBrand={selectedBrand}
        />
      </div>

      <div className="space-y-5 sm:space-y-6">
        <div
          className="ordering-payments__status-rail"
          role="status"
          aria-live="polite"
          aria-busy={
            isPluginsLoading || isLoadingStatus || isTogglingPlugin || undefined
          }
        >
          {isPluginsLoading || isLoadingStatus || isTogglingPlugin ? (
            <div
              className="ordering-payments__status-placeholder"
              data-testid="payments-status-loading"
            />
          ) : isIncomplete ? (
            <span className="ordering-payments__badge" data-status="warning">
              <Icon name="AlertTriangle" size={12} />
              {t("onlinePayments.badges.actionRequired")}
            </span>
          ) : isConnected && stripeConnectPluginEnabled ? (
            <span className="ordering-payments__badge" data-status="success">
              {t("onlinePayments.badges.connected")}
            </span>
          ) : (
            <span className="ordering-payments__badge" data-status="danger">
              {t("onlinePayments.badges.notConnected")}
            </span>
          )}
        </div>

        {isPluginsError ? (
          <div
            className="ordering-payments__error-panel"
            role="alert"
            data-testid="payments-plugin-error"
          >
            <div className="flex items-start gap-3">
              <div className="ordering-payments__error-icon">
                <Icon name="AlertTriangle" size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="ordering-payments__error-title">
                  {t("onlinePayments.error.loadPluginsTitle")}
                </p>
                <p className="ordering-payments__error-copy">
                  {pluginsError?.message || t("onlinePayments.error.tryAgain")}
                </p>
                <div className="mt-4">
                  <Button
                    type="button"
                    aria-label={t("onlinePayments.actions.retryLoadingPlugins")}
                    onClick={() => refetchPlugins()}
                    emphasis="outline"
                    intent="neutral"
                    size="touch"
                    className="ordering-payments__button ordering-payments__button--secondary"
                  >
                    {t("onlinePayments.actions.retry")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {isPluginsLoading || isInstallingPlugin || !hasStripeConnectPlugin ? (
          <LoadingSkeleton />
        ) : !stripeConnectPluginEnabled ? null : isLoadingStatus ? (
          <LoadingSkeleton />
        ) : statusValue === "not_started" ? (
          <div
            className="ordering-payments__state-panel"
            data-state-tone="brand"
          >
            <div className="flex flex-col justify-between gap-8 md:flex-row md:items-start">
              <div className="w-full min-w-0 flex-1 text-start">
                <div className="mb-5 flex items-center gap-3">
                  <div className="ordering-payments__state-icon">
                    <Icon name="CreditCard" size={22} />
                  </div>
                  <h2 className="ordering-payments__state-title">
                    {t("onlinePayments.notStarted.title")}
                  </h2>
                </div>

                <ul className="mb-8 space-y-3">
                  <li className="ordering-payments__benefit">
                    <div className="ordering-payments__benefit-icon">
                      <Icon name="Check" size={14} />
                    </div>
                    <span className="ordering-payments__benefit-copy">
                      {t("onlinePayments.notStarted.bulletSecure", {
                        brand: "Stripe",
                      })}
                    </span>
                  </li>
                  <li className="ordering-payments__benefit">
                    <div className="ordering-payments__benefit-icon">
                      <Icon name="Check" size={14} />
                    </div>
                    <span className="ordering-payments__benefit-copy">
                      {t("onlinePayments.notStarted.bulletPayouts")}
                    </span>
                  </li>
                  <li className="ordering-payments__benefit">
                    <div className="ordering-payments__benefit-icon">
                      <Icon name="Check" size={14} />
                    </div>
                    <span className="ordering-payments__benefit-copy">
                      {t("onlinePayments.notStarted.bulletSetupTime")}
                    </span>
                  </li>
                </ul>

                <StripeActions
                  variant="connect"
                  primaryText={t("onlinePayments.actions.connectStripe")}
                  onPrimaryClick={handleConnectStripe}
                  isPrimaryDisabled={isCreatingLink || isRedirectingToStripe}
                  isPrimaryLoading={isCreatingLink || isRedirectingToStripe}
                  onLearnHowItWorks={() => setIsHowItWorksModalOpen(true)}
                />
              </div>

              <div className="ordering-payments__provider-visual">
                <div>
                  <Icon
                    name="CreditCard"
                    size={64}
                    className="ordering-payments__provider-icon"
                  />
                </div>
              </div>
            </div>
          </div>
        ) : isIncomplete ? (
          <div
            className="ordering-payments__state-panel"
            data-state-tone="warning"
          >
            <div>
              <h2 className="ordering-payments__state-title mb-1">
                {t("onlinePayments.incomplete.title")}
              </h2>
              <p className="ordering-payments__state-subtitle mb-7">
                {t("onlinePayments.incomplete.subtitle")}
              </p>

              {requirements.length > 0 ? (
                <div className="mb-10">
                  <p className="ordering-payments__eyebrow mb-3">
                    {t("onlinePayments.incomplete.requirementsTitle")}
                  </p>
                  <div className="space-y-3">
                    {requirements.map((req) => (
                      <div
                        key={req.key}
                        className="ordering-payments__requirement"
                      >
                        <div className="flex items-center gap-3">
                          <Icon
                            name="AlertCircle"
                            className="ordering-payments__warning-icon"
                            size={18}
                          />
                          <span className="ordering-payments__requirement-copy">
                            {req.label}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {requirements.length > 0 ? (
                <StripeActions
                  variant="primary"
                  primaryText={t("onlinePayments.actions.completeSetup")}
                  onPrimaryClick={handleConnectStripe}
                  isPrimaryDisabled={isCreatingLink || isRedirectingToStripe}
                  isPrimaryLoading={isCreatingLink || isRedirectingToStripe}
                  onRefreshClick={handleRefreshStatus}
                  isRefreshing={isRefreshing}
                />
              ) : (
                <StripeActions
                  variant="primary"
                  primaryText={t("onlinePayments.actions.refreshStatus")}
                  onPrimaryClick={handleRefreshStatus}
                  isPrimaryDisabled={isRefreshing}
                  isPrimaryLoading={isRefreshing}
                />
              )}

              <div className="ordering-payments__state-note mt-7">
                <Icon name="Info" size={14} />
                <p>{t("onlinePayments.incomplete.note")}</p>
              </div>
            </div>
          </div>
        ) : isConnectedButSetupIncomplete ? (
          <div
            className="ordering-payments__state-panel"
            data-state-tone="warning"
          >
            <div>
              <h2 className="ordering-payments__state-title mb-2">
                {t("onlinePayments.setupInProgress.title")}
              </h2>
              <p className="ordering-payments__state-subtitle mb-8 max-w-2xl">
                {t("onlinePayments.setupInProgress.subtitle")}
              </p>
              <p className="ordering-payments__state-note -mt-6 mb-8 max-w-2xl">
                {t("onlinePayments.setupInProgress.note")}
              </p>

              {requirements.length > 0 ? (
                <StripeActions
                  variant="primary"
                  primaryText={t("onlinePayments.actions.continueInStripe")}
                  onPrimaryClick={handleConnectStripe}
                  isPrimaryDisabled={isCreatingLink || isRedirectingToStripe}
                  isPrimaryLoading={isCreatingLink || isRedirectingToStripe}
                  onRefreshClick={handleRefreshStatus}
                  isRefreshing={isRefreshing}
                />
              ) : (
                <StripeActions
                  variant="primary"
                  primaryText={t("onlinePayments.actions.refreshStatus")}
                  onPrimaryClick={handleRefreshStatus}
                  isPrimaryDisabled={isRefreshing}
                  isPrimaryLoading={isRefreshing}
                />
              )}
            </div>
          </div>
        ) : (
          <div
            className="ordering-payments__state-panel"
            data-state-tone="success"
          >
            <div>
              <div className="mb-7 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <h2 className="ordering-payments__state-title">
                    {t("onlinePayments.connected.title")}
                  </h2>
                </div>
                <div className="ordering-payments__success-icon">
                  <Icon name="CheckCircle2" size={22} />
                </div>
              </div>

              <div className="ordering-payments__metrics mb-8">
                <div className="ordering-payments__metric">
                  <p className="ordering-payments__eyebrow mb-1">
                    {t("onlinePayments.connected.accountLabel")}
                  </p>
                  <p className="ordering-payments__metric-value truncate">
                    {status?.email ||
                      t("onlinePayments.connected.accountFallback")}
                  </p>
                </div>
                <div className="ordering-payments__metric">
                  <p className="ordering-payments__eyebrow mb-1">
                    {t("onlinePayments.connected.chargesLabel")}
                  </p>
                  <p
                    className="ordering-payments__metric-value"
                    data-enabled={!!status?.charges_enabled}
                  >
                    {status?.charges_enabled
                      ? t("onlinePayments.connected.enabled")
                      : t("onlinePayments.connected.disabled")}
                  </p>
                </div>
                <div className="ordering-payments__metric">
                  <p className="ordering-payments__eyebrow mb-1">
                    {t("onlinePayments.connected.payoutsLabel")}
                  </p>
                  <p
                    className="ordering-payments__metric-value"
                    data-enabled={!!status?.payouts_enabled}
                  >
                    {status?.payouts_enabled
                      ? t("onlinePayments.connected.enabled")
                      : t("onlinePayments.connected.disabled")}
                  </p>
                </div>
                <div className="ordering-payments__metric">
                  <p className="ordering-payments__eyebrow mb-1">
                    {t("onlinePayments.connected.currencyLabel")}
                  </p>
                  <p className="ordering-payments__metric-value uppercase">
                    {status?.currency || "USD"}
                  </p>
                </div>
              </div>

              <StripeActions
                variant="manage"
                primaryText={t("onlinePayments.actions.manageInStripe")}
                onPrimaryClick={handleConnectStripe}
                isPrimaryDisabled={isCreatingLink || isRedirectingToStripe}
                isPrimaryLoading={isCreatingLink || isRedirectingToStripe}
                onRefreshClick={handleRefreshStatus}
                isRefreshing={isRefreshing}
                onLearnHowItWorks={() => setIsHowItWorksModalOpen(true)}
              />
            </div>
          </div>
        )}

        {!isPluginsLoading &&
        !isInstallingPlugin &&
        hasStripeConnectPlugin &&
        !isLoadingStatus ? (
          <div
            className="ordering-payments__panel overflow-hidden"
            data-payments-active={isPaymentsActive}
          >
            <div className="ordering-payments__panel-header">
              <div className="flex min-w-0 items-center gap-3">
                <div className="ordering-payments__panel-icon">
                  <Icon name="Bot" size={20} />
                </div>
                <div className="min-w-0">
                  <h3 className="ordering-payments__panel-title">
                    {t("onlinePayments.panel.title")}
                  </h3>
                  <p className="ordering-payments__eyebrow mt-1">
                    {t("onlinePayments.panel.subtitle")}
                  </p>
                </div>
              </div>
              <div className="ordering-payments__toggles">
                {showSandboxControl && (
                  <div className="ordering-payments__toggle-group">
                    <span
                      className="ordering-payments__toggle-label"
                      data-active={isSandbox}
                      data-tone="warning"
                    >
                      {t("onlinePayments.panel.sandbox")}
                    </span>
                    <Switch
                      checked={isSandbox}
                      onCheckedChange={handleSandboxChange}
                      className="data-[state=checked]:bg-[var(--warning)]"
                      disabled={!stripeConnectPluginId || isTogglingSandbox}
                      aria-label={t("onlinePayments.actions.toggleSandbox")}
                    />
                  </div>
                )}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="ordering-payments__toggle-group">
                        <span
                          className="ordering-payments__toggle-label"
                          data-active={isPaymentsActive}
                          data-tone="success"
                        >
                          {isPaymentsActive
                            ? t("onlinePayments.panel.statusActive")
                            : t("onlinePayments.panel.statusInactive")}
                        </span>
                        <Switch
                          checked={isPaymentsActive}
                          onCheckedChange={handlePaymentsActiveChange}
                          className="data-[state=checked]:bg-[var(--success)]"
                          disabled={
                            !paymethodId ||
                            isTogglingPaymethod ||
                            (!isPaymentsActive && !isFullyConnected)
                          }
                          aria-label={t(
                            "onlinePayments.actions.toggleOnlinePayments",
                          )}
                        />
                      </div>
                    </TooltipTrigger>
                    {!isPaymentsActive && !isFullyConnected && (
                      <TooltipContent>
                        {t("onlinePayments.panel.activateDisabledReason")}
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            <div className="ordering-payments__panel-body">
              <div className="min-w-0 flex-1">
                <h4 className="ordering-payments__panel-heading mb-5">
                  {t("onlinePayments.panel.heroTitle")}
                </h4>
                <div className="ordering-payments__panel-copy mb-8 space-y-3">
                  <p>{t("onlinePayments.panel.heroDescriptionLine1")}</p>
                  <p>{t("onlinePayments.panel.heroDescriptionLine2")}</p>
                </div>

                <ul className="space-y-3">
                  <li className="ordering-payments__benefit">
                    <div className="ordering-payments__benefit-icon ordering-payments__benefit-icon--small">
                      <Icon name="Check" size={12} />
                    </div>
                    <span className="ordering-payments__benefit-copy">
                      {t("onlinePayments.panel.bulletDynamicLinks")}
                    </span>
                  </li>
                  <li className="ordering-payments__benefit">
                    <div className="ordering-payments__benefit-icon ordering-payments__benefit-icon--small">
                      <Icon name="Check" size={12} />
                    </div>
                    <span className="ordering-payments__benefit-copy">
                      {t("onlinePayments.panel.bulletOrderConfirmation")}
                    </span>
                  </li>
                </ul>
              </div>

              <div className="ordering-payments__preview">
                <div className="ordering-payments__preview-label">
                  <Icon name="MessageSquare" size={14} />
                  <span>{t("onlinePayments.preview.title")}</span>
                </div>

                <div className="space-y-5">
                  <div className="flex gap-3">
                    <div className="ordering-payments__chat-avatar">
                      <Icon name="Bot" size={16} />
                    </div>
                    <div className="ordering-payments__chat-bubble">
                      <p>
                        {t("onlinePayments.preview.chatLineBefore")}{" "}
                        <span className="ordering-payments__chat-product">
                          {t("onlinePayments.preview.sampleProduct")}
                        </span>
                        {t("onlinePayments.preview.chatLineAfter")}
                      </p>
                    </div>
                  </div>

                  <div className="ordering-payments__checkout-card">
                    <div>
                      <div className="mb-5 flex items-center justify-between">
                        <h5 className="ordering-payments__checkout-title">
                          {t("onlinePayments.preview.subtotal")}
                        </h5>
                      </div>
                      <Button
                        type="button"
                        disabled
                        aria-disabled="true"
                        emphasis="solid"
                        intent="brand"
                        size="touch"
                        className="ordering-payments__preview-button"
                        aria-label={t("onlinePayments.preview.payButtonAria")}
                      >
                        {t("onlinePayments.preview.payButton")}
                      </Button>
                      <p className="ordering-payments__preview-note">
                        {t("onlinePayments.preview.payNote")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <PaymentsHowItWorksModal
        isOpen={isHowItWorksModalOpen}
        onClose={() => setIsHowItWorksModalOpen(false)}
      />
    </div>
  );
};

export default OnlinePaymentsSettingsPage;
